import { formConfig } from "../form/formConfig";

/**
 * Detects which fields have changed between current and previous form values
 * @param {Object} currentValues - Current form values
 * @param {Object} previousValues - Previous form values
 * @returns {Array} - Array of changed field names
 */
export const detectChangedFields = (currentValues, previousValues) => {
  if (!previousValues) return [];

  const changedFields = [];

  for (const key of Object.keys(currentValues)) {
    const currentVal = currentValues[key];
    const previousVal = previousValues[key];

    // Handle nested objects (like DIMENSIONS with WIDTH/HEIGHT)
    if (typeof currentVal === 'object' && currentVal !== null) {
      for (const subKey of Object.keys(currentVal)) {
        if (currentVal[subKey] !== previousVal?.[subKey]) {
          changedFields.push(`${key}.${subKey}`);
        }
      }
    } else if (currentVal !== previousVal) {
      changedFields.push(key);
    }
  }

  return changedFields;
};

/**
 * Finds the first changed field that triggers backend communication
 * Returns the top-level field name (e.g., "DIMENSIONS" not "DIMENSIONS.WIDTH")
 * @param {Object} currentValues - Current form values
 * @param {Object} previousValues - Previous form values
 * @returns {string|null} - Field name that triggered the change, or null
 */
export const findChangedBackendField = (currentValues, previousValues) => {
  if (!previousValues) return null;

  for (const key of Object.keys(currentValues)) {
    const currentVal = currentValues[key];
    const previousVal = previousValues[key];
    const config = formConfig.find(c => c.name === key);

    if (!config) continue;

    // Check if this field has backend communication
    const hasBackend = config.backendType === 'option' ||
                       config.backendType === 'replace' ||
                       config.backendKey ||
                       (config.type === 'dimensionPicker' && config.fields &&
                        Object.values(config.fields).some(f => f.backendKey));

    if (!hasBackend) continue;

    // Check if value changed
    if (typeof currentVal === 'object' && currentVal !== null) {
      for (const subKey of Object.keys(currentVal)) {
        if (currentVal[subKey] !== previousVal?.[subKey]) {
          return key; // Return top-level field name
        }
      }
    } else if (currentVal !== previousVal) {
      return key;
    }
  }

  return null;
};

/**
 * Gets the config for a field by name (supports nested fields like DIMENSIONS.WIDTH)
 * @param {string} fieldPath - Field path (e.g., "DIMENSIONS.WIDTH" or "WAND_DIKTE")
 * @returns {Object} - { config, fieldConfig, subField } or null
 */
const getFieldConfig = (fieldPath) => {
  const parts = fieldPath.split('.');
  const fieldName = parts[0];
  const subField = parts[1] || null;

  const config = formConfig.find(c => c.name === fieldName);
  if (!config) return null;

  // For nested fields (dimensionPicker)
  if (subField && config.fields) {
    return {
      config,
      fieldConfig: config.fields[subField],
      subField
    };
  }

  return { config, fieldConfig: config, subField: null };
};

/**
 * Collects all current options and replaces from form values
 * @param {Object} formValues - Current form values
 * @returns {Object} - { options: [], replace: [] }
 */
const collectAllOptionsAndReplaces = (formValues) => {
  const result = {
    options: [],
    replace: []
  };

  formConfig.forEach((config) => {
    if (config.type === 'subtitle') return;

    const currentValue = formValues[config.name];
    if (currentValue === undefined || currentValue === '') return;

    if (config.backendType === 'option') {
      const selectedOption = config.options?.find(opt => opt.value === currentValue);
      if (selectedOption?.optionKey) {
        result.options.push(selectedOption.optionKey);
      }
    }
    else if (config.backendType === 'replace') {
      const selectedOption = config.options?.find(opt => opt.value === currentValue);
      if (selectedOption?.replaceKey && config.replaceElement) {
        result.replace.push({
          element: config.replaceElement,
          new_type: selectedOption.replaceKey
        });
      }
    }
  });

  return result;
};

/**
 * Checks if a field is an option or replace type
 * @param {string} fieldPath - Field path
 * @returns {boolean}
 */
const isOptionOrReplaceField = (fieldPath) => {
  const fieldInfo = getFieldConfig(fieldPath);
  if (!fieldInfo) return false;
  return fieldInfo.config.backendType === 'option' || fieldInfo.config.backendType === 'replace';
};

/**
 * Builds a WebSocket message for a single field change
 * @param {string} fieldPath - The field that changed (e.g., "DIMENSIONS.WIDTH" or "MATERIAL_WAND")
 * @param {Object} formValues - Current form values
 * @returns {Object|null} - WebSocket message or null if field should not be sent
 */
export const buildSingleFieldMessage = (fieldPath, formValues) => {
  const fieldInfo = getFieldConfig(fieldPath);
  if (!fieldInfo) return null;

  const { config, fieldConfig, subField } = fieldInfo;

  // Skip fields without backend communication
  if (config.type === 'subtitle') return null;

  // Get the current value
  let currentValue;
  if (subField) {
    currentValue = formValues[config.name]?.[subField];
  } else {
    currentValue = formValues[config.name];
  }

  if (currentValue === undefined || currentValue === '') return null;

  const message = {
    action: "generate",
    model: "Optie_Tuinh2"
  };

  // Handle different backend types
  if (config.backendType === 'option' || config.backendType === 'replace') {
    // For option/replace changes: include ALL current options and replaces
    const allOptionsAndReplaces = collectAllOptionsAndReplaces(formValues);

    message.option_solver = {};
    if (allOptionsAndReplaces.options.length > 0) {
      message.option_solver.options = allOptionsAndReplaces.options;
    }
    if (allOptionsAndReplaces.replace.length > 0) {
      message.option_solver.replace = allOptionsAndReplaces.replace;
    }

    // If no options or replaces, don't send
    if (Object.keys(message.option_solver).length === 0) {
      return null;
    }
  }
  else if (fieldConfig?.backendKey || config.backendKey) {
    // Regular parameter - only send the parameter, no option_solver
    const backendKey = fieldConfig?.backendKey || config.backendKey;
    message.parameters = {
      [backendKey]: currentValue
    };
  }
  else {
    // No backend communication for this field
    return null;
  }

  return message;
};

/**
 * Checks if any of the changed fields is an option or replace type
 * @param {Array} changedFields - Array of changed field paths
 * @returns {boolean}
 */
export const hasOptionOrReplaceChange = (changedFields) => {
  return changedFields.some(fieldPath => isOptionOrReplaceField(fieldPath));
};

/**
 * Builds a full WebSocket message with all parameters (for initial load)
 * @param {Object} formValues - Current form values
 * @returns {Object} - WebSocket message with all parameters
 */
export const buildFullMessage = (formValues) => {
  const message = {
    action: "generate",
    model: "Optie_Tuinh2",
    option_solver: {
      options: [],
      replace: []
    },
    parameters: {}
  };

  formConfig.forEach((config) => {
    // Skip non-input fields
    if (config.type === 'subtitle') return;

    const currentValue = formValues[config.name];
    if (currentValue === undefined || currentValue === '') return;

    // Handle option type
    if (config.backendType === 'option') {
      const selectedOption = config.options?.find(opt => opt.value === currentValue);
      if (selectedOption?.optionKey) {
        message.option_solver.options.push(selectedOption.optionKey);
      }
    }
    // Handle replace type
    else if (config.backendType === 'replace') {
      const selectedOption = config.options?.find(opt => opt.value === currentValue);
      if (selectedOption?.replaceKey && config.replaceElement) {
        message.option_solver.replace.push({
          element: config.replaceElement,
          new_type: selectedOption.replaceKey
        });
      }
    }
    // Handle dimensionPicker (nested fields)
    else if (config.type === 'dimensionPicker' && config.fields) {
      Object.entries(config.fields).forEach(([fieldKey, fieldConfig]) => {
        if (fieldConfig.backendKey && currentValue?.[fieldKey] !== undefined) {
          message.parameters[fieldConfig.backendKey] = currentValue[fieldKey];
        }
      });
    }
    // Handle regular parameters
    else if (config.backendKey) {
      message.parameters[config.backendKey] = currentValue;
    }
  });

  // Cleanup: remove empty objects
  if (message.option_solver.options.length === 0) {
    delete message.option_solver.options;
  }
  if (message.option_solver.replace.length === 0) {
    delete message.option_solver.replace;
  }
  if (Object.keys(message.option_solver).length === 0) {
    delete message.option_solver;
  }
  if (Object.keys(message.parameters).length === 0) {
    delete message.parameters;
  }

  return message;
};

/**
 * Legacy function - maps form values to backend format (for backwards compatibility)
 * @deprecated Use buildFullMessage or buildSingleFieldMessage instead
 */
export const mapFormToBackend = (formValues) => {
  const message = buildFullMessage(formValues);
  return message.parameters || {};
};
